import React, { useEffect, useMemo, useRef, useState } from "react";
import { addDays } from "date-fns/addDays";
import { differenceInDays } from "date-fns/differenceInDays";
import { dateToString } from "@/helpers";
import IconRight from "$/icons/icon-right.svg";
import IconLeft from "$/icons/icon-left.svg";
import { CalendarDates, HorizontalCalendarProps } from "@/components/HorizontalCalendar/HorizontalCalendarInterface";
import styles from "./HorizontalCalendar.module.scss";

const HorizontalCalendar = (props: HorizontalCalendarProps) => {
  const { firstDate, lastDate, highlightedDates, dottedDates, userAgent, handleTsdEvent } = props;

  // Create a ref array for all the elements
  const elementRefs = useRef<HTMLDivElement[]>([]); // Array of refs
  const rootRef = useRef<HTMLDivElement>();
  const [visibleMonth, setVisibleMonth] = useState<string | null>(null);

  const groupDatesByMonth = (datesArray) => {
    return datesArray.reduce((acc, dateString) => {
      const month = dateToString(new Date(dateString), "MMMM");

      // If the month doesn't exist in the accumulator, create an empty array
      if (!acc[month]) {
        acc[month] = [];
      }

      acc[month].push(dateString);

      return acc;
    }, {});
  };

  const handleScroll: (type: "left" | "right") => void = (type) => {
    const dateWidth: number = 48;
    const dateGap: number = 10;
    const scrollWidth: number = dateWidth + dateGap;

    rootRef.current.scrollTo({
      left: rootRef.current.scrollLeft + (type === "left" ? -scrollWidth : scrollWidth),
      behavior: "smooth",
    });
  };

  // Get current month's dates
  const calendarDates: CalendarDates = useMemo(() => {
    const firstDay = new Date(firstDate);
    const lastDay = new Date(lastDate);

    // Calculate the total number of days
    const dayCount = differenceInDays(lastDay, firstDay);
    // Generate an array of dates
    const dates = Array.from({ length: dayCount }, (_, index) => addDays(firstDay, index));
    const monthWiseDates = groupDatesByMonth(dates);

    return monthWiseDates;
  }, [firstDate, lastDate]);

  // Check if a date is selected
  const isDateHighlighted = (date) =>
    highlightedDates.some(
      (selectedDate) => dateToString(selectedDate, "dd-MM-yyyy") === dateToString(date, "dd-MM-yyyy")
    );

  // Check if a date has a dotted circle
  const isDateDotted = (date) =>
    dottedDates.some((dotedDate) => dateToString(dotedDate, "dd-MM-yyyy") === dateToString(date, "dd-MM-yyyy"));

  const calendarDatesEntries = Object.entries(calendarDates);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        entries.forEach((entry: IntersectionObserverEntry, index) => {
          if (entry.target instanceof HTMLElement && entry.isIntersecting) {
            setVisibleMonth(entry.target?.dataset.month);
          }
        });
      },
      {
        root: rootRef?.current,
        rootMargin: "0% -50% 0% -50%",
        threshold: 0,
      }
    );

    // Observe all elements
    elementRefs.current.forEach((element) => {
      if (element) observer.observe(element);
    });

    return () => {
      observer.disconnect(); // Cleanup observer
    };
  }, []);

  return (
    <div className={styles.wrapper}>
      {userAgent?.isDesktop && (
        <div className={styles.arrows}>
          <button
            type={"button"}
            onClick={() => {
              if (handleTsdEvent && typeof handleTsdEvent === "function") {
                handleTsdEvent("left");
              }

              handleScroll("left");
            }}
            className={styles.arrowLeft}
          >
            <IconLeft />
          </button>
          <button
            type={"button"}
            onClick={() => {
              if (handleTsdEvent && typeof handleTsdEvent === "function") {
                handleTsdEvent("right");
              }

              handleScroll("right");
            }}
            className={styles.arrowRight}
          >
            <IconRight />
          </button>
        </div>
      )}
      <h2 className={`${styles.title}`}>{visibleMonth}</h2>
      <div className={styles.container} ref={rootRef}>
        <>
          {calendarDatesEntries.map(([month, monthDates], index) => {
            return (
              <div
                key={month}
                data-month={month}
                ref={(el) => {
                  if (el) elementRefs.current[index] = el;
                }}
                className={styles.sectionBx}
              >
                {/* Horizontal Scrollable Calendar */}
                <div className={styles.calendarScroll}>
                  {monthDates.map((date) => {
                    // Determine date classes dynamically
                    const dateClasses = [styles.dateItem];
                    if (isDateHighlighted(date)) dateClasses.push(styles.selected);
                    if (isDateDotted(date)) dateClasses.push(styles.dotted);

                    return (
                      <div key={date.toISOString()} className={styles.dateWrapper}>
                        <span className={styles.dayAbbr}>{dateToString(new Date(date), "EEEEE")}</span>
                        <div className={dateClasses.join(" ")}>{date.getDate()}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </>
      </div>
    </div>
  );
};

export default HorizontalCalendar;
